import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { useMemo, useRef, useLayoutEffect } from "react";
import { Center } from "@react-three/drei";

function visibleSizeAtZ(camera) {
  const vFOV = (camera.fov * Math.PI) / 180;
  const height = 2 * Math.tan(vFOV / 2) * Math.abs(camera.position.z);
  const width = height * camera.aspect;
  return { width, height };
}

function fitAndCenter(group, target) {
  const box = new THREE.Box3().setFromObject(group);
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getCenter(center);
  box.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z || 1);
  const scale = maxDim > 0 ? target / maxDim : 1;
  group.scale.setScalar(scale);
  group.position.sub(center.multiplyScalar(scale)); // pivote al origen
}

function Scene({ svgUrl, depth, fallbackColor = "#1741C9", speed = 0.6 }) {
  const data = useLoader(SVGLoader, svgUrl);
  const spinRef = useRef();
  const logoRef = useRef();
  const { camera, size } = useThree();

  const logoGroup = useMemo(() => {
    const g = new THREE.Group();
    const paths = data?.paths ?? [];
    const extrude = {
      depth,
      bevelEnabled: true,
      bevelSize: 0.6,
      bevelThickness: 0.6,
      bevelSegments: 2,
    };

    for (const p of paths) {
      const fill = p.userData?.style?.fill;
      const useFill =
        fill && fill !== "none" ? new THREE.Color(fill) : (p.color || new THREE.Color(fallbackColor));
      const shapes = p.toShapes(true);
      for (const s of shapes) {
        const geom = new THREE.ExtrudeGeometry(s, extrude);
        geom.computeVertexNormals();
        const mat = new THREE.MeshPhysicalMaterial({
          color: useFill,
          metalness: 0.2,
          roughness: 0.28,
          clearcoat: 0.7,
          clearcoatRoughness: 0.2,
          side: THREE.FrontSide, // evita auto-sombras internas
        });
        const mesh = new THREE.Mesh(geom, mat);
        mesh.castShadow = true;
        mesh.receiveShadow = false;
        g.add(mesh);
      }
    }

    // endereza si venía “de cabeza”
    g.rotation.z = Math.PI;
    return g;
  }, [data, depth, fallbackColor]);

  // Encaja SIEMPRE al tamaño del canvas: usa el menor de ancho/alto (90%)
useLayoutEffect(() => {
  let raf1 = 0, raf2 = 0;
  const recalc = () => {
    if (!logoRef.current) return;
    const { width, height } = visibleSizeAtZ(camera);
    const target = Math.min(width, height) * 0.9; // 90% del lado corto
    // clamp defensivo
    const safe = Number.isFinite(target) && target > 0 ? target : 10;
    fitAndCenter(logoRef.current, safe);
  };
  // espera un frame y recalcula (dos rAF para estar seguros)
  raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(recalc); });
  return () => {
    cancelAnimationFrame(raf1);
    cancelAnimationFrame(raf2);
  };
}, [camera, size.width, size.height, depth]);

  useFrame((_, dt) => {
    if (spinRef.current) spinRef.current.rotation.y += dt * speed;
  });

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight
        position={[25, 35, 40]}
        intensity={1.05}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-normalBias={0.02}
        shadow-bias={-0.0005}
      />
      <directionalLight position={[-20, -10, 30]} intensity={0.4} />
      <group ref={spinRef}>
        <primitive object={logoGroup} ref={logoRef} />
      </group>
      <OrbitControls enableDamping dampingFactor={0.08} enableZoom={false} />
    </>
  );
}

export default function Logo3D({ svgUrl, color = "#1741C9", depth = 18 }) {
  return (
    <Canvas
      shadows
      camera={{ position: [0, 0, 80], fov: 40 }}
      onCreated={({ gl }) => {
        gl.setPixelRatio(Math.min(2, window.devicePixelRatio || 1)); // 👈 evita “zooms” raros
      }}
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      <Scene svgUrl={svgUrl} depth={depth} fallbackColor={color} />
    </Canvas>
  );
}